<template>
    <div
        class="card relative flex min-w-0 flex-col text-sm"
        data-test="file-card"
    >
        <!-- Stretched over the card, as on FileListRow, so the menu beside it stays clickable. -->
        <button
            type="button"
            class="rounded-muval-3 absolute inset-0"
            :aria-label="`Open ${label}`"
            :title="label"
            @click="emit('open')"
        ></button>

        <div class="rounded-t-muval-3 pointer-events-none relative aspect-[4/3] overflow-hidden border-b">
            <FileThumbnail
                :file="file"
                :label="label"
                :failed="thumbnailFailed"
                :originals-readable="originalThumbnails"
                :resolve-thumbnail-url="resolveThumbnailUrl"
                @error="emit('image-error')"
            />
        </div>

        <div class="pointer-events-none relative flex min-w-0 flex-col gap-y-1 px-3 pt-2 pb-3">
            <div class="flex min-w-0 items-center justify-between gap-x-1">
                <span
                    class="truncate"
                    data-test="file-card-label"
                    >{{ label }}</span
                >
                <div class="text-muval-gray-1 pointer-events-auto -mr-1 flex shrink-0 items-center text-xs">
                    <FileActionsMenu
                        :can-download="canDownload"
                        :can-edit="canEdit"
                        :can-delete="canDelete"
                        @download="emit('download')"
                        @edit="emit('edit')"
                        @delete="emit('delete')"
                    >
                        <template
                            v-if="$slots['menu-items']"
                            #menu-items="{ run }"
                        >
                            <slot
                                name="menu-items"
                                :run="run"
                            />
                        </template>
                    </FileActionsMenu>
                </div>
            </div>
            <span
                v-if="file.created_at"
                class="text-muval-gray-3 text-xs"
                >{{ uploadedAt }}</span
            >
            <!-- Its hoverable chips catch the pointer, so a click on one opens the file too. -->
            <FileChips
                :file="file"
                single-line
                class="mt-1"
                @click="emit('open')"
            >
                <slot name="chips" />
            </FileChips>
        </div>
    </div>
</template>

<script setup lang="ts">
import FileActionsMenu from '@components/files/FileActionsMenu.vue';
import FileChips from '@components/files/FileChips.vue';
import FileThumbnail from '@components/files/FileThumbnail.vue';
import useDayJs from '@plugins/useDayJs';
import { useUserStore } from '@store/user';
import { getFileDisplayName } from '@utilities/fileDisplay';

/** One stored file in a list's thumbnail view: its picture, label, upload time, chips and menu. */

const props = withDefaults(
    defineProps<{
        file: Record<string, any>;
        /** Overrides the label where a collection has a name of its own. */
        label?: string;
        canDownload?: boolean;
        canEdit?: boolean;
        canDelete?: boolean;
        /** The thumbnail has already failed after a refetch, so stop trying it. */
        thumbnailFailed?: boolean;
        /** False where `url` points at a private object, so the thumbnail never tries it. */
        originalThumbnails?: boolean;
        /** Signs a link for an image whose original isn't readable. */
        resolveThumbnailUrl?: (file: Record<string, any>) => Promise<string>;
    }>(),
    {
        label: undefined,
        canDownload: true,
        canEdit: false,
        canDelete: false,
        thumbnailFailed: false,
        originalThumbnails: true,
        resolveThumbnailUrl: undefined,
    },
);

const emit = defineEmits<{ open: []; download: []; edit: []; delete: []; 'image-error': [] }>();

const dayjs = useDayJs();
const userStore = useUserStore();

const label = computed(() => props.label || getFileDisplayName(props.file));

const uploadedAt = computed(() => {
    const timezone = userStore.getTimezone;
    const at = dayjs(props.file.created_at);

    return (timezone ? at.tz(timezone) : at).format('DD/MM/YY h:mmA');
});
</script>
